import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { nutrition } from '../../api/Endpoints';
import { QUERY_KEYS } from '../../api/config';
import {
  AllFoodsList,
  BookmarkSupplementData,
  FoodDetailParam,
  FoodMeta,
  GetAllFoodsParam,
  GetFoodRankResponse,
  SaveFoodData,
  Supplement,
  SupplementDetailParam,
  SupplementListData,
  IFoodProductResponse,
  FoodDetailData,
  SupplementDetail,
  IDailyServingDataResponse,
  BookmarkFoodMutationContext,
  IFoodMetaDetailResponse,
  FoodProductQueryParam,
  NutritionTipsResponse,
} from './Nutrition.model';
import { getBookmarkedItems } from './Nutrition.utils';

/**
 *
 * @param queryParam - Parameters for the API request
 * @param selectedSubFilters - // Array of filter IDs selected by the user
 */
export const useGetAllFoodQuery = (
  queryParam?: GetAllFoodsParam, // Parameters for the API request
  selectedSubFilters?: number[], // Array of filter IDs selected by the user
) => {
  return useQuery<AllFoodsList, Error, FoodMeta[]>({
    queryKey: [
      QUERY_KEYS.ALL_FOODS,
      ...(queryParam ? [queryParam] : []), // queryParam is optional
    ],

    queryFn: async () => {
      const response = await nutrition.getAllFood(queryParam);
      return response.data; // Return the data as is, matching the AllFoodsList type
    },

    select: ({ food }) => {
      if (!selectedSubFilters) return food;

      const subFiltersCount = selectedSubFilters.length; // Total number of selected filters

      // Determine if saved or main filters are applied
      const hasSavedFilter = selectedSubFilters?.includes(0) ?? false; // Check for the "saved" filter
      const hasMainFilter = selectedSubFilters?.includes(-1) ?? false; // Check for the "main" filter
      const isSpecialFilterApplied = hasSavedFilter || hasMainFilter; // Check if either filter is applied

      // Precompute the list of saved foods if the saved filter is active
      const savedFoods = hasSavedFilter
        ? food.filter((item) => item.is_saved)
        : [];

      // Case 1: Return only saved foods if saved filter is active and it's the only filter applied
      if (hasSavedFilter && savedFoods.length > 0 && subFiltersCount === 1) {
        return savedFoods;
      }

      // Case 2: Base list for filtering - use saved foods if the saved filter is applied, otherwise use all foods
      const baseFilteredList = hasSavedFilter ? savedFoods : food;

      // Filter the base list by rank using the selected sub-filters
      const filteredResult = baseFilteredList.filter((item) =>
        selectedSubFilters?.includes(item.rank),
      );

      // If filtered results are available, return them
      if (filteredResult.length > 0) {
        return filteredResult;
      }

      // Case 3: Handle special filter logic
      if (isSpecialFilterApplied) {
        // Return all foods if the main filter is active and it's the only filter applied
        if (hasMainFilter && subFiltersCount === 1) {
          return food;
        }

        // If both main and saved filters are active, return saved foods
        if (hasMainFilter && hasSavedFilter) {
          return savedFoods;
        }

        // If no matches, return an empty list
        return [];
      }

      // Default Case: Return the full list of foods when no filters match
      return food;
    },

    // Use the previous query data as placeholder while fetching new data
    placeholderData: keepPreviousData,
  });
};

export const useGetRankQuery = (
  options?: Partial<UseQueryOptions<GetFoodRankResponse, Error>>,
) => {
  return useQuery({
    ...options,
    queryKey: [QUERY_KEYS.FOOD_RANK],
    queryFn: async () => {
      const response = await nutrition.getFoodRank();
      return response.data;
    },
  });
};

export const useGetMyNutritionTipsQuery = () => {
  return useQuery<NutritionTipsResponse, Error>({
    queryKey: [QUERY_KEYS.NUTRITION_TIPS],
    queryFn: async () => {
      const response = await nutrition.getMyNutritionTips();
      return response.data;
    },
  });
};

export const useGetSupplementListQuery = (
  queryParam?: GetAllFoodsParam,
  selectedSubFilters?: number[],
) => {
  return useQuery<SupplementListData, Error, Supplement[]>({
    queryKey: [QUERY_KEYS.ALL_SUPPLEMENTS, ...(queryParam ? [queryParam] : [])],
    queryFn: async () => {
      const response = await nutrition.getSupplementList(queryParam);
      return response.data;
    },
    select: ({ supplement }: { supplement: Supplement[] }) => {
      const subFiltersCount = selectedSubFilters?.length ?? 0;
      const hasSavedFilter = selectedSubFilters?.includes(0) ?? false;
      const hasMainFilter = selectedSubFilters?.includes(-1) ?? false;
      const isSpecialFilterApplied = hasSavedFilter || hasMainFilter;
      const savedSupplements = hasSavedFilter
        ? getBookmarkedItems(supplement)
        : [];
      if (
        hasSavedFilter &&
        savedSupplements.length > 0 &&
        subFiltersCount === 1
      ) {
        return savedSupplements;
      }
      if (isSpecialFilterApplied) {
        if (hasMainFilter && subFiltersCount === 1) {
          return supplement;
        }
        if (hasMainFilter && hasSavedFilter) {
          return savedSupplements;
        }
        return [];
      }
      return supplement;
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetDailyServingDataQuery = (
  options?: Partial<UseQueryOptions<IDailyServingDataResponse, Error>>,
) => {
  return useQuery({
    ...options,
    queryKey: [QUERY_KEYS.DAILY_SERVING_DATA],
    queryFn: async () => {
      const response = await nutrition.getDailyServingData();
      return response.data;
    },
  });
};

export const useSaveFoodMutation = (
  options?: Partial<UseMutationOptions<null, Error, SaveFoodData>>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data: SaveFoodData) => {
      return nutrition.saveFood(data);
    },
    onMutate: async (newBookMark) => {
      const res = newBookMark?.flag === 'ADD';
      const keys = newBookMark?.key
        ? [QUERY_KEYS.ALL_FOODS, newBookMark?.key]
        : [QUERY_KEYS.ALL_FOODS];

      await queryClient.cancelQueries({
        queryKey: keys,
      });
      const previousFoods = queryClient.getQueryData<AllFoodsList>(keys);

      if (previousFoods) {
        // Update the cached data
        const updatedFoodList = previousFoods.food?.map((item) =>
          item.id === newBookMark.food_id ? { ...item, is_saved: res } : item,
        );
        queryClient.setQueryData(keys, {
          ...previousFoods,
          food: updatedFoodList,
        });
      }
    },
    onError: (_err, _newBookMark, context) => {
      if (context && 'previousFoods' in context) {
        queryClient.setQueryData(
          [QUERY_KEYS.ALL_FOODS],
          (context as BookmarkFoodMutationContext).previousFoods,
        );
      }
    },
    onSettled: (__, _, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_FOODS] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.FOOD_DETAILS, variables.food_id],
      });
    },
  });
};

export const useGetFoodFilterQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.FOOD_FILTER],
    queryFn: async () => {
      const response = await nutrition.getFoodFilter();
      return response.data;
    },
  });
};

export const useGetSupplementFilterQuery = (product_id: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SUPPLEMENT_FILTER, product_id],
    queryFn: async () => {
      const response = await nutrition.getSupplementFilter(product_id);
      return response.data;
    },
    select: (data) => {
      const filterResponse = [data];
      return filterResponse;
    },
  });
};

export const useGetFoodDetailQuery = (param: FoodDetailParam) => {
  return useQuery<FoodDetailData>({
    queryKey: [QUERY_KEYS.FOOD_DETAILS, param.food_id],
    queryFn: async () => {
      const response = await nutrition.getFoodDetail(param);
      return response.data;
    },
    enabled: !!param.food_id,
  });
};

export const useSaveSupplementMutation = (
  options?: Partial<UseMutationOptions<null, Error, BookmarkSupplementData>>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data: BookmarkSupplementData) => {
      return nutrition.saveSupplement(data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_SUPPLEMENTS] });
    },
  });
};

export const useGetSupplementDetailQuery = (param: SupplementDetailParam) => {
  return useQuery<SupplementDetail>({
    queryKey: [QUERY_KEYS.SUPPLEMENT_DETAILS, param.ingredient_id],
    queryFn: async () => {
      const response = await nutrition.getSupplementDetail(param);
      return response.data;
    },
  });
};

export const useGetFoodMetaDetailQuery = (
  param: FoodDetailParam,
  options?: Partial<UseQueryOptions<IFoodMetaDetailResponse, Error>>,
) => {
  return useQuery({
    ...options,
    queryKey: [QUERY_KEYS.FOOD_META_DETAILS, param.food_id],
    queryFn: async () => {
      const response = await nutrition.getFoodMetaDetail(param);
      return response.data;
    },
  });
};

export const useGetProductsQuery = (params?: FoodProductQueryParam) => {
  return useQuery<IFoodProductResponse>({
    queryKey: [
      QUERY_KEYS.FOOD_PRODUCTS,
      ...(params ? [JSON.stringify(params)] : []),
    ],
    queryFn: async () => {
      const response = await nutrition.getProducts(params);
      return response.data;
    },
  });
};
